import { Money, MoneyValue } from '@ads-bread/shared/bread/codecs';

/**
 * Converts number to usd money object.
 *
 * @param value - A number to convert.
 *
 * @returns - Returns money object.
 *
 */
export function usd(value: number): Money {
  return {
    currency: 'USD',
    value: toMoneyValue(value),
  };
}

/**
 * Converts number to cad money object.
 *
 * @param value - A number to convert.
 *
 * @returns - Returns money object.
 *
 */
export function cad(value: number): Money {
  return {
    currency: 'CAD',
    value: toMoneyValue(value),
  };
}

/**
 * Creates money object.
 *
 * @param value - The value of the currency.
 * @param currency - The currency code.
 *
 * @returns - Returns money object.
 *
 */
export function toMoneyObj(value: number, currency: string): Money {
  return {
    currency,
    value: toMoneyValue(value),
  };
}

/**
 * Formats money object as string representation of currency.
 *
 * @param money - The value of the currency.
 *
 * @returns - Returns string representation of currency.
 *
 */
export function formatMoney(money: Money): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency,
  }).format(money.value / 100);
}

/**
 * Converts number to a money value object.
 *
 * @param value - The value of the currency.
 *
 * @returns - Returns money value object.
 *
 */
export const toMoneyValue = (value: number): MoneyValue => {
  if (value !== undefined && isNaN(value)) {
    return toMoneyValue(0);
  }

  if (!isMoneyValue(value)) {
    throw new Error('value must be an integer');
  }

  return value;
};

/**
 * Determine if a value is of Money type
 * @param val
 * @returns boolean
 */
export const isMoney = (val: number | Money): val is Money => {
  return (
    typeof val === 'object' &&
    typeof val.currency === 'string' &&
    Number.isInteger(val.value)
  );
};

/**
 * Determine if value is a money value. This is a type guard
 * for a money value with branded integer type.
 *
 * @param value - The value of the currency.
 *
 * @returns - Returns if value is a money value.
 *
 */
export const isMoneyValue = (
  value: number | undefined
): value is MoneyValue => {
  return Number.isInteger(value) || value === Infinity;
};
